import React, { FunctionComponent, useEffect, useState } from 'react';
import Table from 'antd/lib/table';
import { useDispatch, useSelector } from 'react-redux';

import { ICourse, ICourseDict, selectAreCoursesFetching, selectCoursesDict } from '../../../../../features/courses/coursesSlice';
import { ILessonDict, selectAreLessonsFetching, selectLessonsDict } from '../../../../../features/lesson/lessonSlice';
import { LearningType, IFulfillment, LearningLabels } from '../../../../../features/reports/learning/common/interfaces';
import { ITopicDict, selectAreTopicsFetching, selectTopics } from '../../../../../features/topic/topicSlice';
import { getColumnByType, getHeadersToExport, getHeadersToExportForCourseCompletion, getRowToExport } from './ColumnsPerType';
import { RootState } from '../../../../../app/store';
import { StatusEnum } from '../../../../common/Status';
import { selectAreUsersFetching, selectStudentIdPerGroup, selectStudents } from '../../../../../features/users/usersSlice';
import { selectAreProgramsFetching, selectProgramsDict } from '../../../../../features/program/programSlice';
import { selectCompanies, selectCompaniesAreFetching } from '../../../../../features/company/companySlice';
import { selectAreGroupsFetching, selectCompaniesGroups, selectGroupsDict } from '../../../../../features/groups/groupSlice';
import { selectEvaluationsDict, selectAreEvaluationFetching } from '../../../../../features/evaluation/evaluationsSlice';
import { IGroupsChildren } from '../interfaces';
import { selectCompaniesContentsAreFetching } from '../../../../../features/reports/learning/common/companiesContent';
import { IEvaluationsCounters } from '../../../../../features/reports/learning/common/interfaces';

import styles from './index.module.scss';
import tableStyles from "../../../../common/table.module.scss";
import { ITableRecord } from './interfaces';
import { EvaluationTypeEnum } from '../../../../../features/evaluation/common/enums';
import { selectSurveyResultsCounters, selectSurveyResultsCountersIsFetching } from '../../../../../features/reports/learning/courses/surveyResultsCounters';
import { getTranslations } from '../../../../../features/translations/translationsUtils';
import { Button } from 'antd';
import { CogIcon } from '../../../../common/CogIcon';
import { DataType, exportToExcel } from '../../../common/excelExport';
import { fetchProgressPerUser, selectCoursesProgressPerUser, selectCoursesProgressPerUserFetching } from '../../../../../features/reports/learning/courses/progressPerUser';
import { shouldSkipRebuild } from './shouldSkipRebuild';


interface ContentTableProps {
  companyId: string;
  learningType: LearningType;
  selectFulfillments: (state: RootState) => IFulfillment[];
  selectFulfillmentsFetching: (state: RootState) => boolean;
  selectEvaluationsResultsCounters?: (state: RootState) => IEvaluationsCounters[];
  selectEvaluationsResultsCountersIsFetching?: (state: RootState) => boolean;
  selectedGroupId: string | undefined;
}
const PAGE_SIZE = 10;

export const ContentTable: FunctionComponent<ContentTableProps> = ({
  learningType,
  companyId,
  selectFulfillments,
  selectFulfillmentsFetching,
  selectEvaluationsResultsCounters = (state: RootState) => ([]),
  selectEvaluationsResultsCountersIsFetching = (state: RootState) => false,
  selectedGroupId,
}) => {
  const evaluationsResultsCounters = useSelector(selectEvaluationsResultsCounters);
  const evaluationsResultsCountersIsFetching = useSelector(selectEvaluationsResultsCountersIsFetching);
  const surveysResultsCounters = useSelector(selectSurveyResultsCounters);
  const surveysResultsCountersIsFetching = useSelector(selectSurveyResultsCountersIsFetching);
  const contentsAreFetching = useSelector(selectCompaniesContentsAreFetching);
  const [groupsChildren, setGroupsChildren] = useState<IGroupsChildren>({});
  const [groupsChildrenSet, setGroupsChildrenSet] = useState<boolean>(false);
  const [content, setContent] = useState<ITableRecord[]>([]);
  const [contentSet, setContentSet] = useState<boolean>(false);
  const [groupUseForBuiltTable, setGroupUseForBuiltTable] = useState<string>('');
  // Item 43/49 (SYSTEM.debt.md): `contentSet` alone never tracked which
  // companyId the table was
  // built for, so switching companyId (e.g. same session filtering a
  // different Company, or a different role viewing the same Company) left
  // the table frozen on stale data — same pattern already used above for
  // `groupUseForBuiltTable`/`selectedGroupId`.
  const [companyUsedForBuiltTable, setCompanyUsedForBuiltTable] = useState<string>('');
  // Groups
  const groups = useSelector(selectGroupsDict);
  const companiesGroups = useSelector(selectCompaniesGroups);
  const groupFetching = useSelector(selectAreGroupsFetching);
  // Companies
  const companies = useSelector(selectCompanies);
  const companiesFetching = useSelector(selectCompaniesAreFetching);
  // Users
  const students = useSelector(selectStudents);
  const studentIdPerGroup = useSelector(selectStudentIdPerGroup);
  const studentsFetching = useSelector(selectAreUsersFetching);
  // Fulfillments/Content
  const isFulfillmentsFetching = useSelector(selectFulfillmentsFetching);
  const fulfillments = useSelector(selectFulfillments);
  //evaluations
  const evaluations = useSelector(selectEvaluationsDict);
  const isEvaluationFetching = useSelector(selectAreEvaluationFetching);
  // Lessons
  const lessonsFetching = useSelector(selectAreLessonsFetching);
  // Topics
  const topicsFetching = useSelector(selectAreTopicsFetching);
  // Courses
  const coursesFetching = useSelector(selectAreCoursesFetching);
  // Programs
  const programsFetching = useSelector(selectAreProgramsFetching);
  const programs = useSelector(selectProgramsDict);
  const coursesProgressPerUserFetching = useSelector(selectCoursesProgressPerUserFetching);
  const coursesProgressPerUser = useSelector(selectCoursesProgressPerUser);
  //LearningContent
  const learningContents: {
    lessons: ILessonDict;
    topics: ITopicDict;
    courses: ICourseDict;
  } = {
    lessons: useSelector(selectLessonsDict),
    topics: useSelector(selectTopics),
    courses: useSelector(selectCoursesDict),
  };
  const dispatch = useDispatch();

  useEffect(() => {
    if (learningType !== LearningType.courses) {
      return;
    }
    if (companyId && !studentsFetching) {
      const usersIds = Object.values(students).filter(student => student.company === companyId)
        .map(student => student.username);
      if (usersIds.length > 0) {
        dispatch(fetchProgressPerUser(usersIds));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, companyId, studentsFetching, learningType]);

  const learningContent = learningContents[learningType];
  const columns = getColumnByType(learningType, selectedGroupId);
  const learningContentFetching = lessonsFetching || topicsFetching || coursesFetching || programsFetching || contentsAreFetching || groupFetching;

  const fetching = studentsFetching ||
    learningContentFetching ||
    isFulfillmentsFetching ||
    companiesFetching ||
    groupFetching ||
    isEvaluationFetching ||
    evaluationsResultsCountersIsFetching ||
    surveysResultsCountersIsFetching;
  const courses = learningContents.courses;
  const topics = learningContents.topics;
  useEffect(() => {
    setGroupsChildrenSet(false);
  }, [companyId]);
  useEffect(() => {
    if (groupsChildrenSet || learningContentFetching || Object.keys(courses).length === 0) {
      return;
    }
    const groupsChildren: IGroupsChildren = {};
    Object.keys(courses).forEach((courseId) => {
      const groupsIds: string[] = Object.values(groups)
        .filter((group) => group.courses.includes(courseId))
        .map((group) => group._id);
      const course = courses[courseId];
      if (!course) {
        return;
      }
      const topicsIds: string[] = course.topics;
      const lessonsIds: string[] = [];
      course.topics.forEach((topicId) => {
        const topic = topics[topicId];
        if (topic) {
          lessonsIds.push(...topic.lessons);
        }
      });
      groupsIds.forEach((groupId) => {
        if (!groupsChildren[groupId]) {
          groupsChildren[groupId] = {
            courses: [],
            topics: [],
            lessons: [],
            students: 0,
          };
        }
        groupsChildren[groupId].lessons.push(...lessonsIds);
        groupsChildren[groupId].courses.push(courseId);
        groupsChildren[groupId].topics.push(...topicsIds);
      });
    });
    setGroupsChildrenSet(true);
    setGroupsChildren(groupsChildren);
  }, [
    courses,
    groups,
    topics,
    learningContentFetching,
    groupsChildrenSet,
    companyId,
  ]);
  useEffect(() => {
    const data: ITableRecord[] = [];
    if (
      fetching
    ) {
      setContentSet(false);
      return;
    }
    if (shouldSkipRebuild({
      contentSet,
      groupsChildrenSet,
      coursesCount: Object.keys(courses).length,
      studentIdPerGroupCount: Object.keys(studentIdPerGroup).length,
      groupUseForBuiltTable,
      selectedGroupId,
      companyUsedForBuiltTable,
      companyId,
    })) {
      return;
    }
    if (groupUseForBuiltTable !== selectedGroupId) {
      setGroupUseForBuiltTable(selectedGroupId ?? '');
    }
    if (companyUsedForBuiltTable !== companyId) {
      setCompanyUsedForBuiltTable(companyId);
    }
    const getContentsIds = (coursesIds: string[], learningType: LearningType): { [id: string]: number } => {
      const contentsIds = ((coursesIds: string[], learningType: LearningType): string[] => {
        if (learningType === LearningType.courses) {
          return coursesIds;
        }
        if (learningType === LearningType.topics) {
          const topicsIds: string[] = [];
          coursesIds.forEach(courseId => {
            topicsIds.push(...learningContents.courses[courseId].topics)
          });
          return topicsIds;
        }
        const lessonsIds: string[] = [];
        coursesIds.forEach(courseId => {
          learningContents.courses[courseId].topics.forEach(topicId => {
            if (!learningContents.topics[topicId]) {
              return;
            }
            lessonsIds.push(...learningContents.topics[topicId].lessons)
          })
        })
        return lessonsIds;
      })(coursesIds, learningType);
      const results: { [id: string]: number } = {};
      contentsIds.forEach(contentId => {
        if (!results[contentId]) {
          results[contentId] = 0;
        }
        ++results[contentId];
      })
      return results;
    };
    const map: {
      [id: string]: IFulfillment[];
    } = {};
    fulfillments.forEach(fulfillment => {
      const studentGroupId = students[fulfillment.username]?.studentGroupId;
      const company = students[fulfillment.username]?.company;
      if (!studentGroupId || !company) {
        return;
      }
      if (map[`${studentGroupId}-${company}-${fulfillment.id}`] === undefined) {
        map[`${studentGroupId}-${company}-${fulfillment.id}`] = [];
      }

      if (fulfillment.completed > 0) {
        map[`${studentGroupId}-${company}-${fulfillment.id}`].push(fulfillment);
      }
    });
    Object.keys(companies).forEach(itemCompanyId => {
      if (companyId && companyId !== itemCompanyId) {
        return;
      }
      const company = companies[itemCompanyId] || {};
      if (!company.studyProgram) {
        return;
      }
      const program = programs[company.studyProgram];
      if (!program) {
        return;
      }
      if (!companiesGroups[itemCompanyId]) {
        return;
      }
      const companyGroups = Array.from(new Set(companiesGroups[itemCompanyId] || []));
      const contentsTotals: { [id: string]: { total: number, completed: number } } = {}
      companyGroups.forEach(companyGroupId => {
        const group = groups[companyGroupId];
        if (!group || (selectedGroupId !== undefined && selectedGroupId !== companyGroupId)) {
          return;
        }
        const contentsIds = getContentsIds(group.courses, learningType);
        Object.keys(contentsIds).forEach(contentId => {
          if (!contentsTotals[contentId]) {
            contentsTotals[contentId] = {
              total: 0,
              completed: 0,
            };
          }
          const groupContents = groupsChildren[companyGroupId];
          if (!groupContents || !groupContents[learningType].includes(contentId)) {
            return;
          }
          const contentFulfillments = map[`${companyGroupId}-${itemCompanyId}-${contentId}`] || [];
          contentsTotals[contentId].completed += contentFulfillments.reduce((accumulator, currentValue) => accumulator + currentValue.completed, 0);
          contentsTotals[contentId].total += studentIdPerGroup[companyGroupId] ? studentIdPerGroup[companyGroupId].length : 0;
        });
      });
      let evaluationType: EvaluationTypeEnum;
      switch (learningType) {
        case LearningType.courses:
          evaluationType = EvaluationTypeEnum.Exam;
          break;
        case LearningType.topics:
          evaluationType = EvaluationTypeEnum.Test;
          break;
        case LearningType.lessons:
          evaluationType = EvaluationTypeEnum.Quiz;
          break;
      }
      Object.keys(contentsTotals).forEach(contentId => {
        const content = learningContent[contentId] || {};
        const { total, completed } = contentsTotals[contentId];
        const evaluation = evaluations[content.evaluation || ''];
        let score: number | undefined = undefined;
        const usersIds: string[] = [];
        if (evaluation) {
          score = 0;
          let total = 0;
          let passed = 0;
          const counters = evaluationsResultsCounters.filter(evaluationResultCounter =>
            evaluationResultCounter._id.parentId === contentId &&
            students[evaluationResultCounter._id.username]?.company === itemCompanyId
          );
          counters.forEach(evaluationResultCounter => {
            usersIds.push(evaluationResultCounter._id.username);
            total += evaluationResultCounter.total;
            passed += evaluationResultCounter.passed;
          });
          if (total > 0) {
            score = passed / total * 100;
          }
        }
        const record: any = {
          company: company.name,
          contentName: (learningContent[contentId] || {}).title,
          evaluation: evaluation ? evaluation.title : ' -- ',
          fulfillment: (total > 0 ? completed / total * 100 : 0),
          key: `${company._id}-${contentId}`,
          status: content.status || StatusEnum.Disable,
          score,
          contentId,
          companyId: itemCompanyId,
          evaluationId: content.evaluation,
          evaluationType,
          contentType: learningType,
        };
        if (learningType === LearningType.courses) {
          const counters = surveysResultsCounters.filter(surveyResultsCounter => surveyResultsCounter._id.parentId === contentId);
          const evaluation = evaluations[(content as ICourse).survey || ''];
          record.survey = evaluation ? evaluation.title : ' -- ';
          record.surveyId = evaluation ? evaluation._id : ' -- ';
          let total = 0;
          if (counters.length > 0) {
            counters.forEach(evaluationResultCounter => {
              total += evaluationResultCounter.total;
            });
          }
          record.completed = total ? 100 : 0;
        }
        data.push(record)
      });
    });
    setContentSet(true);
    setContent(data);
  }, [selectedGroupId, groupUseForBuiltTable, companyUsedForBuiltTable, companies, companyId, companiesGroups, contentSet, courses, evaluations, evaluationsResultsCounters, fetching, fulfillments, groups, groupsChildren, groupsChildrenSet, learningContent, learningContents.courses, learningContents.topics, learningType, programs, studentIdPerGroup, students, surveysResultsCounters]);
  const handleDownload = () => {
    const headers = [...getHeadersToExport(learningType)];
    const data: DataType = content.map((row) => {
      return getRowToExport(row, learningType);
    });

    exportToExcel(
      headers,
      data,
      'Cumplimiento',
      'cumplimiento'
    );
  };

  const handleCourseCompletionDownload = () => {
    const coursesNames: string[] = content.map((row) => {
      return row.contentName;
    });
    const coursesIds: string[] = content.map((row) => {
      return row.contentId;
    });
    const headers = [...getHeadersToExportForCourseCompletion(coursesNames)];
    const studentsData = Object.values(students).filter(student => student.company === companyId);
    const data: DataType = studentsData.map((row) => {
      return [
        companies[row.company ?? '']?.name || ' -- ',
        row.email || ' -- ',
        row.nationalId || ' -- ',
        row.name,
        row.status === 'FORCE_CHANGE_PASSWORD' ? 'No registrado' : 'Registrado',
        row.enabled ? 'Habilitado' : 'Deshabilitado',
        groups[row.studentGroupId ?? '']?.name || ' -- ',
        row.city || ' -- ',
        row.country || ' -- ',
        ...(coursesProgressPerUser ? coursesIds.map(contentId => {
          const contentProgress = coursesProgressPerUser?.[row.username]?.[contentId];
          return contentProgress ? `${contentProgress * 100}%` : ' -- ';
        }) : []),
      ];
    });

    exportToExcel(
      headers,
      data,
      'Completacion',
      'Completacion'
    );
  };
  const renderCourseCompletionButton = () => {
    if (learningType !== LearningType.courses) {
      return null;
    }
    return <Button
      className={styles.downloadButton}
      disabled={coursesProgressPerUserFetching || !companyId}
      ghost={true}
      onClick={handleCourseCompletionDownload}
    >
      {getTranslations('REPORT_DOWNLOAD_COURSE_COMPLETION')} <CogIcon size="1.4em" color="#81858b" icon={"Download"} />
    </Button>;
  };

  return <div className={styles.container}>
    <div className={styles.title}>
      <span>{getTranslations('REPORT_FINISH_CONTENT_PER_COMPANY', [getTranslations(LearningLabels[learningType])])}</span>
      <Button
        className={styles.downloadButton}
        disabled={!companyId}
        ghost={true}
        onClick={handleDownload}
      >
        {getTranslations('REPORT_DOWNLOAD')} <CogIcon size="1.4em" color="#81858b" icon={"Download"} />
      </Button>
      {renderCourseCompletionButton()}
    </div>
    <Table<ITableRecord>
      loading={fetching}
      className={tableStyles.table}
      rowClassName={() => tableStyles.row}
      rowKey="key"
      columns={columns}
      dataSource={content}
      scroll={{ x: 2000, y: 500 }}
      pagination={{ position: ["bottomRight"], pageSize: PAGE_SIZE, showSizeChanger: false }}
    />
  </div>
};